import React, { Component } from 'react';
import CompareTestInstances from '../presentation/CompareTestInstances/CompareTestInstances';
import { getComparisonFields, getComparisonMeta, getComparisonAnalyticsFields } from '../../util/helpers';

/**
 * Container for the "Compare Test Instances" view (BD-8219).
 *
 * Receives the ids of the selected completed test instances via
 * `pageParameters`, fetches the full details (and scenario name/environment
 * where required) for each, computes the relevant comparison fields, and hands
 * the result to the presentation component.
 *
 * Also exposes the grouping/SDK-filter settings (BD-8220, BD-8221) and the
 * save/update/delete operations for named comparisons (BD-8222) to the
 * presentation component.
 */
class CompareTestInstancesWidget extends Component {
    constructor(props) {
        super(props);

        this.isUnmounted = false;
        this.state = {
            isLoading: true,
            instances: [],
            error: '',
        };
    }

    componentDidMount() {
        this.loadInstances();
    }

    componentWillUnmount() {
        this.isUnmounted = true;
    }

    getTestInstanceIds() {
        const { pageParameters } = this.props;
        if (!Array.isArray(pageParameters)) {
            return [];
        }
        // De-duplicate while preserving selection order.
        return pageParameters.filter((id, index) => id && pageParameters.indexOf(id) === index);
    }

    async fetchTestInstance(testInstanceId) {
        const response = await fetch(
            '/service/getSingleTestInstanceDetails?testInstanceId=' + testInstanceId,
            {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'same-origin',
            }
        );

        if (response.status !== 200) {
            throw new Error('Failed to fetch test instance ' + testInstanceId);
        }

        return response.json();
    }

    async fetchScenarioDetails(scenarioId) {
        if (!scenarioId) {
            return { name: '', environment: '', zones: [] };
        }

        const response = await fetch('/service/getScenarioDetails?scenarioId=' + scenarioId, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
        });

        if (response.status !== 200) {
            return { name: '', environment: '', zones: [] };
        }

        const scenario = await response.json();
        return {
            name: scenario.name || '',
            environment: scenario.environment || '',
            zones: Array.isArray(scenario.zones)
                ? scenario.zones.map(zone => ({ _id: zone.zoneId, name: zone.zoneName }))
                : [],
        };
    }

    async fetchClusterDevices(deviceIds) {
        if (!deviceIds.length) {
            return [];
        }

        const response = await fetch('/service/getClusterDevices?devices=' + deviceIds, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
        });

        if (response.status !== 200) {
            return [];
        }

        return response.json();
    }

    async fetchTriggerEvents(testInstanceId) {
        const response = await fetch('/service/triggerEvents/' + testInstanceId + '&checkIn', {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
        });

        if (response.status !== 200) {
            return [];
        }

        return response.json();
    }

    // Fetch the devices and check-in trigger data needed to compute the analytics
    // comparison rows (hit/exit rates, TTR/TTT responsiveness, dwell/checkout
    // counts, SDK DPH). Mirrors the mapping done in MatrixAnalysisWidget. Returns
    // null on any failure so the comparison still renders with "N/A" analytics.
    async fetchAnalytics(testInstance, zones) {
        try {
            const deviceIds = Array.isArray(testInstance.devices)
                ? testInstance.devices.map(device => device.deviceId).filter(Boolean)
                : [];

            const [devices, results] = await Promise.all([
                this.fetchClusterDevices(deviceIds),
                this.fetchTriggerEvents(testInstance._id),
            ]);

            const triggerResults = Array.isArray(results) ? results : [];

            const triggers = triggerResults.map(trigger => ({
                deviceId: trigger.identifier,
                zoneId: trigger.zoneId,
                zoneName: trigger.zoneName,
                receivedAt: trigger.receivedAt && trigger.receivedAt.value,
                eventTime: trigger.triggerEvents && trigger.triggerEvents.eventTime,
                shouldExit: typeof trigger.zoneName === 'string' && trigger.zoneName.endsWith('CO'),
                didExit: !!(trigger.exitTriggers && trigger.exitTriggers.length),
            }));

            const dwellEvents = triggerResults
                .filter(trigger => trigger.dwellEvents || trigger.dwellReceived === 1)
                .map(trigger => ({
                    deviceId: trigger.identifier,
                    zoneId: trigger.zoneId,
                    zoneName: trigger.zoneName,
                }));

            return {
                devices: Array.isArray(devices) ? devices : [],
                zones: zones || [],
                triggers,
                dwellEvents,
            };
        } catch (error) {
            return null;
        }
    }

    async loadInstances() {
        const ids = this.getTestInstanceIds();

        if (ids.length < 2) {
            this.setState({
                isLoading: false,
                instances: [],
                error: 'Select at least two completed test instances to compare.',
            });
            return;
        }

        try {
            const instances = await Promise.all(
                ids.map(async id => {
                    const testInstance = await this.fetchTestInstance(id);

                    let scenarioName = '';
                    let scenarioEnvironment = '';
                    let zones = [];
                    // Newer instances embed scenario details (including zones);
                    // only fetch them separately for older instances that do not.
                    if (testInstance.scenario) {
                        zones = Array.isArray(testInstance.scenario.zones)
                            ? testInstance.scenario.zones.map(zone => ({ _id: zone.zoneId, name: zone.zoneName }))
                            : [];
                    } else if (testInstance.scenarioId) {
                        const scenario = await this.fetchScenarioDetails(testInstance.scenarioId);
                        scenarioName = scenario.name;
                        scenarioEnvironment = scenario.environment;
                        zones = scenario.zones;
                    }

                    const analytics = await this.fetchAnalytics(testInstance, zones);

                    return {
                        id,
                        name: testInstance.name || id,
                        fields: [
                            ...getComparisonFields(testInstance, scenarioName, scenarioEnvironment),
                            ...getComparisonAnalyticsFields(analytics),
                        ],
                        meta: getComparisonMeta(testInstance, scenarioName),
                    };
                })
            );

            if (!this.isUnmounted) {
                this.setState({ isLoading: false, instances, error: '' });
            }
        } catch (error) {
            if (!this.isUnmounted) {
                this.setState({
                    isLoading: false,
                    instances: [],
                    error: 'An error has occurred fetching test instance details for comparison.',
                });
            }
        }
    }

    // Persist the current comparison (selected instances + grouping/SDK filter)
    // under a name so it can be reopened later (BD-8222).
    saveComparison = async ({ name, groupBy, sdkFilter }) => {
        const response = await fetch('/service/saveComparison', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                name,
                groupBy,
                sdkFilter,
                testInstanceIds: this.getTestInstanceIds(),
            }),
        });

        if (response.status !== 200) {
            throw new Error('Failed to save comparison');
        }

        return response.json();
    };

    // Update an existing saved comparison in place (BD-8222).
    updateComparison = async (comparisonId, { name, groupBy, sdkFilter }) => {
        const response = await fetch('/service/updateComparison', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                comparisonId,
                name,
                groupBy,
                sdkFilter,
                testInstanceIds: this.getTestInstanceIds(),
            }),
        });

        if (response.status !== 200) {
            throw new Error('Failed to update comparison');
        }

        return response.json();
    };

    // Delete a saved comparison (BD-8222).
    deleteComparison = async comparisonId => {
        const response = await fetch('/service/deleteComparison', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify({ comparisonId }),
        });

        if (response.status !== 200) {
            throw new Error('Failed to delete comparison');
        }

        return response.json();
    };

    render() {
        return (
            <CompareTestInstances
                navigator={this.props.navigator}
                instances={this.state.instances}
                isLoading={this.state.isLoading}
                error={this.state.error}
                options={this.props.options || {}}
                onSaveComparison={this.saveComparison}
                onUpdateComparison={this.updateComparison}
                onDeleteComparison={this.deleteComparison}
            />
        );
    }
}

export default CompareTestInstancesWidget;
